// ═══════════════════════════════════════════════════════════════════════════════
// Join Pairing Page (Helper — Enter Pairing Code)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../lib/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/ToastProvider';
import { AppButton } from '../../components/ui/AppButton';
import { NotificationBell } from '../../components/NotificationBell';
import { MainMenuButton } from '../../components/MainMenuButton';
import { PAIRING_CODE_EXPIRY_MINUTES, PAIRING_CODE_LENGTH } from '@fdh/shared';
import { formatPairingExpiryDurationLabel } from '../../lib/pairingCodeExpiry';
import type { UserPublic } from '@fdh/shared';
import type { PairingStatusResponse } from '@fdh/shared';
import { PLAY_BANNER_ABSOLUTE_TOP_4_CLASS } from '../../lib/layoutChrome';

type JoinResult = {
  pairingId: string;
  employer: UserPublic;
};

type JoinLocationState = { pairingError?: string };

export const JoinPairingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { refreshUser } = useAuth();
  const { showSuccessToast } = useToast();
  const [codeChars, setCodeChars] = useState<string[]>(
    Array(PAIRING_CODE_LENGTH).fill('')
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    const msg = (location.state as JoinLocationState | null)?.pairingError;
    if (!msg) return;
    setError(msg);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const checkExistingPairing = async () => {
      try {
        const response = await api.get<PairingStatusResponse>('/pairing/status');
        if (!response.success || !response.data?.isPaired) return;
        await refreshUser();
        navigate('/pairing/status', { replace: true });
      } catch {
        // Keep current page when status check fails.
      }
    };
    void checkExistingPairing();
  }, [navigate, refreshUser]);

  const handleInputChange = useCallback(
    (index: number, value: string) => {
      const char = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (!char && value !== '') return;

      const newChars = [...codeChars];
      newChars[index] = char;
      setCodeChars(newChars);
      setError('');

      if (char && index < PAIRING_CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [codeChars]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !codeChars[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        const newChars = [...codeChars];
        newChars[index - 1] = '';
        setCodeChars(newChars);
      }
    },
    [codeChars]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData
        .getData('text')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, PAIRING_CODE_LENGTH);

      if (!pasted) return;

      const newChars = [...codeChars];
      for (let i = 0; i < pasted.length; i++) {
        newChars[i] = pasted[i];
      }
      setCodeChars(newChars);

      const focusIdx = Math.min(pasted.length, PAIRING_CODE_LENGTH - 1);
      inputRefs.current[focusIdx]?.focus();
    },
    [codeChars]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const code = codeChars.join('');
      if (code.length !== PAIRING_CODE_LENGTH) {
        setError(t('pairing.join.errorIncomplete', { length: PAIRING_CODE_LENGTH }));
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        const response = await api.post<JoinResult>('/pairing/join', { code });

        if (!response.success) {
          throw new Error(response.error ?? t('pairing.join.errorGeneric'));
        }

        await refreshUser();
        showSuccessToast(t('toast.pairing.joinSuccess'));
        navigate('/pairing/success', {
          state: {
            pairingId: response.data?.pairingId ?? '',
            employer: response.data?.employer,
          },
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : t('pairing.join.errorGeneric'));
      } finally {
        setIsLoading(false);
      }
    },
    [codeChars, navigate, refreshUser, t]
  );

  const isCodeComplete = codeChars.every((c) => c !== '');

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className={`absolute right-4 z-10 flex items-center gap-2 ${PLAY_BANNER_ABSOLUTE_TOP_4_CLASS}`}>
        <MainMenuButton />
        <NotificationBell />
      </div>
      <div className="w-full max-w-md">
        <div className="rounded-2xl bg-white p-8 shadow-lg">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-8 w-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <h1
              className="text-2xl font-bold text-gray-900"
              role="heading"
              aria-level={1}
            >
              {t('pairing.join.title')}
            </h1>
            <p className="mt-2 text-sm text-gray-500">{t('pairing.join.subtitle')}</p>
          </div>

          {error && (
            <div
              className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-700"
              role="alert"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div
              className="mb-6 flex justify-center gap-2 sm:gap-3"
              role="group"
              aria-label={t('pairing.join.inputGroupAria')}
            >
              {codeChars.map((char, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="text"
                  maxLength={1}
                  value={char}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="h-14 w-11 rounded-lg border-2 border-gray-300 bg-gray-50 text-center font-mono text-2xl font-bold uppercase text-gray-900 transition-colors focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-200 focus:outline-none sm:h-16 sm:w-13"
                  aria-label={t('pairing.join.charAria', { n: index + 1 })}
                  tabIndex={0}
                  disabled={isLoading}
                  autoComplete="off"
                />
              ))}
            </div>

            <AppButton
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={isLoading || !isCodeComplete}
              aria-label={t('pairing.join.submitAria')}
              className="px-6"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t('pairing.join.pairing')}
                </span>
              ) : (
                t('pairing.join.submit')
              )}
            </AppButton>
          </form>

          <p className="mt-6 text-center text-xs text-gray-400">
            {t('pairing.join.footer', {
              duration: formatPairingExpiryDurationLabel(PAIRING_CODE_EXPIRY_MINUTES, t),
            })}
          </p>
        </div>
      </div>
    </div>
  );
};
